/**
 * The three tiers, as a number or nothing at all.
 *
 *   1        covered — audited, and the only ones the site quotes figures for
 *   0        red — rejected, and a reason is required
 *   absent   grey — held or listed, not yet audited
 *
 * Grey is omitted because it is the majority (67 of 127) and the same
 * default-by-omission the gap list and the currency use: state the common case
 * once, carry only the departures.
 *
 * A caution that belongs next to the encoding rather than in a commit message:
 * red is `0`, which is falsy. `if (record.tier)` reads a rejected fund as
 * having no tier. Use `tierOf` rather than testing the raw value.
 */
export type TierCode = 1 | 0 | undefined;
export type Tier = 'covered' | 'grey' | 'red';

export const tierOf = (code: TierCode): Tier => (code === 1 ? 'covered' : code === 0 ? 'red' : 'grey');

export const codeForTier = (tier: Tier): TierCode => (tier === 'covered' ? 1 : tier === 'red' ? 0 : undefined);

/** True only for the audited ones. Written out because `!!code` is wrong for
    red and `code != null` is wrong for grey. */
export const isCovered = (code: TierCode): boolean => code === 1;

/**
 * What each tier is called in front of a reader.
 *
 * `covered` is the internal word and stays the internal word — it describes the
 * state (we have covered it) and it collides with "covered call" everywhere it
 * appears in copy. In public it is the **CleanList**, which is the brand and is
 * the thing people will ask for by name.
 *
 * Here rather than in the copy so the name has one definition. It is going to
 * appear on the home page, the screener, the rankings and every fund page, and a
 * brand renamed in four of five places is worse than one never renamed.
 */
export const TIER_LABEL: Record<Tier, string> = {
    covered: 'CleanList',
    grey: 'Grey list',
    red: 'Red list',
};
